import { useEffect, useState } from 'react';
import api from '../services/api';
import useLang from '../hooks/useLang';
import useAuthStore from '../stores/authStore';

const PHONE_PATTERN = /^255\d{9}$/;

function normalizePhone(phone) {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) return `255${digits.slice(1)}`;
  return digits;
}

const ROLE_COLORS = {
  superadmin: 'bg-red-100 text-red-700',
  admin:      'bg-blue-100 text-blue-700',
  member:     'bg-gray-100 text-gray-600',
};

export default function Users() {
  const { T } = useLang();
  const U = T.users;
  const { user: me } = useAuthStore();

  const [users, setUsers]       = useState([]);
  const [groups, setGroups]     = useState([]);
  const [loading, setLoading]   = useState(true);

  // create modal
  const [showCreate, setShowCreate]   = useState(false);
  const [createForm, setCreateForm]   = useState({ name: '', phone: '', email: '', password: '', role: 'member', group_id: '' });
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError]   = useState('');

  // edit modal
  const [editTarget, setEditTarget]   = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [editSaving, setEditSaving]   = useState(false);
  const [editError, setEditError]     = useState('');

  // add-to-group modal
  const [atgTarget, setAtgTarget]   = useState(null); // user object
  const [atgForm, setAtgForm]       = useState({ group_id: '', role: 'mwanachama' });
  const [atgSaving, setAtgSaving]   = useState(false);
  const [atgError, setAtgError]     = useState('');
  const [atgSuccess, setAtgSuccess] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/groups').then(r => setGroups(r.data));
  }, []);

  /* ---------- CREATE ---------- */
  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    const phone = normalizePhone(createForm.phone);
    if (!PHONE_PATTERN.test(phone)) { setCreateError(U.errPhone); return; }
    setCreateSaving(true);
    try {
      await api.post('/users', { ...createForm, phone });
      setShowCreate(false);
      setCreateForm({ name: '', phone: '', email: '', password: '', role: 'member', group_id: '' });
      load();
    } catch (err) {
      setCreateError(err.response?.data?.error || U.errCreate);
    } finally {
      setCreateSaving(false);
    }
  };

  /* ---------- EDIT ---------- */
  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ role: u.role, status: u.status, password: '', group_id: u.group_id || '' });
    setEditError('');
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    const payload = { role: editForm.role, status: editForm.status, group_id: editForm.group_id };
    if (editForm.password) payload.password = editForm.password;
    setEditSaving(true);
    try {
      await api.put(`/users/${editTarget.id}`, payload);
      setEditTarget(null);
      load();
    } catch (err) {
      setEditError(err.response?.data?.error || U.errSave);
    } finally {
      setEditSaving(false);
    }
  };

  /* ---------- ADD TO GROUP ---------- */
  const openAtg = (u) => {
    setAtgTarget(u);
    setAtgForm({ group_id: '', role: 'mwanachama' });
    setAtgError('');
    setAtgSuccess('');
  };

  const handleAtg = async (e) => {
    e.preventDefault();
    setAtgError('');
    setAtgSuccess('');
    setAtgSaving(true);
    try {
      const res = await api.post('/users/add-to-group', {
        phone:    atgTarget.phone,
        group_id: atgForm.group_id,
        role:     atgForm.role,
      });
      setAtgSuccess(res.data.message || U.addToGroupSuccess);
      load();
    } catch (err) {
      setAtgError(err.response?.data?.error || U.errAddToGroup);
    } finally {
      setAtgSaving(false);
    }
  };

  /* ---------- DELETE ---------- */
  const handleDelete = async (id, name) => {
    if (!confirm(`${U.deleteConfirm} "${name}"?`)) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const roleLabel = { superadmin: U.superadmin, admin: U.adminGroup, member: U.memberRole };
  const groupName = (gid) => groups.find(g => String(g.id) === String(gid))?.name || '—';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">{U.title}</h1>
        <button onClick={() => setShowCreate(true)}
          className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium">
          {U.addUser}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-500">{T.common.loading}</div>
      ) : (
        <div className="space-y-3">
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {users.map(u => (
              <div key={u.id} className="rounded-[24px] border border-white/75 bg-white/88 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-800">{u.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{u.phone}</p>
                    <p className="mt-0.5 text-xs text-slate-400">{groupName(u.group_id)}</p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => openEdit(u)}
                      className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                      {T.common.edit}
                    </button>
                    <button onClick={() => openAtg(u)}
                      className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {U.addToGroup}
                    </button>
                    {me?.id !== u.id && (
                      <button onClick={() => handleDelete(u.id, u.name)}
                        className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">
                        {T.common.delete}
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{U.roleLabel}</p>
                    <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                      {roleLabel[u.role] || u.role}
                    </span>
                  </div>
                  <div className="rounded-2xl bg-slate-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{U.status}</p>
                    <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {u.status === 'active' ? U.active : U.inactive}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {!users.length && <div className="text-center py-8 text-gray-400">{U.noUsers}</div>}
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500 text-xs uppercase">
                  <th className="text-left px-4 py-3">{T.common.name}</th>
                  <th className="text-left px-4 py-3">{T.common.phone}</th>
                  <th className="text-left px-4 py-3">{U.group}</th>
                  <th className="text-left px-4 py-3">{U.roleLabel}</th>
                  <th className="text-left px-4 py-3">{U.status}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                    <td className="px-4 py-3 text-gray-500">{u.phone}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{groupName(u.group_id)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}>
                        {roleLabel[u.role] || u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {u.status === 'active' ? U.active : U.inactive}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => openEdit(u)}
                          className="text-blue-500 hover:text-blue-700 text-xs font-medium">
                          {T.common.edit}
                        </button>
                        <button onClick={() => openAtg(u)}
                          className="text-emerald-600 hover:text-emerald-800 text-xs font-medium">
                          {U.addToGroup}
                        </button>
                        {me?.id !== u.id && (
                          <button onClick={() => handleDelete(u.id, u.name)}
                            className="text-red-400 hover:text-red-600 text-xs">
                            {T.common.delete}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!users.length && (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-400">{U.noUsers}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---- CREATE MODAL ---- */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5">{U.newUser}</h2>
            {createError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{createError}</div>
            )}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.fullName} *</label>
                <input required type="text" value={createForm.name}
                  onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.phone} *</label>
                <input required type="tel" value={createForm.phone} placeholder="255715465042"
                  onChange={e => { setCreateForm({ ...createForm, phone: e.target.value }); setCreateError(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
                <p className="text-xs text-gray-400 mt-1">{U.phoneNote}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.password} *</label>
                <input required type="password" value={createForm.password}
                  onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{U.role}</label>
                  <select value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm">
                    <option value="superadmin">{U.superadmin}</option>
                    <option value="admin">{U.adminGroup}</option>
                    <option value="member">{U.memberRole}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{U.group}</label>
                  <select value={createForm.group_id} onChange={e => setCreateForm({ ...createForm, group_id: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm">
                    <option value="">{U.groupSelect}</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setCreateError(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit" disabled={createSaving}
                  className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-60">
                  {createSaving ? U.creating : U.create}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---- ADD TO GROUP MODAL ---- */}
      {atgTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1">{U.addToGroupTitle}</h2>
            <p className="text-sm text-gray-500 mb-5">{atgTarget.name} · {atgTarget.phone}</p>
            <p className="text-xs text-gray-400 mb-4">{U.addToGroupSub}</p>

            {atgError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{atgError}</div>
            )}
            {atgSuccess && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-3 text-sm mb-4">{atgSuccess}</div>
            )}

            <form onSubmit={handleAtg} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.group} *</label>
                <select required value={atgForm.group_id}
                  onChange={e => setAtgForm({ ...atgForm, group_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm">
                  <option value="">{U.groupSelect}</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.selectRole} *</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(U.memberRoles).map(([val, label]) => (
                    <button key={val} type="button"
                      onClick={() => setAtgForm({ ...atgForm, role: val })}
                      className={`py-2.5 px-3 rounded-lg text-xs font-medium border transition-colors text-left ${
                        atgForm.role === val
                          ? 'bg-emerald-600 border-emerald-600 text-white'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setAtgTarget(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit" disabled={atgSaving}
                  className="flex-1 bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-800 disabled:opacity-60">
                  {atgSaving ? U.addingToGroup : U.addToGroupBtn}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---- EDIT MODAL ---- */}
      {editTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1">{U.editUser}</h2>
            <p className="text-sm text-gray-500 mb-5">{editTarget.name} · {editTarget.phone}</p>
            {editError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{editError}</div>
            )}
            <form onSubmit={handleEdit} className="space-y-4">
              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.role}</label>
                <div className="grid grid-cols-3 gap-2">
                  {['superadmin', 'admin', 'member'].map(r => (
                    <button key={r} type="button"
                      onClick={() => setEditForm({ ...editForm, role: r })}
                      className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                        editForm.role === r
                          ? r === 'superadmin' ? 'bg-red-600 border-red-600 text-white'
                            : r === 'admin' ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-gray-700 border-gray-700 text-white'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}>
                      {roleLabel[r]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.status}</label>
                <div className="grid grid-cols-2 gap-2">
                  {['active', 'inactive'].map(s => (
                    <button key={s} type="button"
                      onClick={() => setEditForm({ ...editForm, status: s })}
                      className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                        editForm.status === s
                          ? s === 'active' ? 'bg-green-600 border-green-600 text-white'
                            : 'bg-red-600 border-red-600 text-white'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}>
                      {s === 'active' ? U.active : U.inactive}
                    </button>
                  ))}
                </div>
              </div>

              {/* Group */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.group}</label>
                <select value={editForm.group_id} onChange={e => setEditForm({ ...editForm, group_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm">
                  <option value="">{U.groupSelect}</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>

              {/* New password (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{U.newPassword} <span className="text-gray-400 font-normal">({U.optional})</span></label>
                <input type="password" value={editForm.password}
                  onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder={U.passwordPlaceholder}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditTarget(null)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit" disabled={editSaving}
                  className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-60">
                  {editSaving ? T.common.saving : T.common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
